/* eslint-disable react/prop-types */
import * as React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Today from '@mui/icons-material/Today';
import Task from '@mui/icons-material/Task';
import {
  Box,
  Button,
  InputAdornment,
  Paper,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import { useNavigate } from 'react-router-dom';
import { secondary } from '../../../utils/brandColors';

// Custom PaperComponent to include a footer
const CustomPaper = (props) => {
  const {
    children,
    includeHorseTaskTypeTagId,
    includeBarnTaskTypeTagId,
    includeEventTaskTypeTagId,
    ...other
  } = props;
  const navigate = useNavigate();
  return (
    <Paper {...other}>
      {children}
      <div
        style={{
          padding: '8px',
          borderTop: '1px solid #eee',
          display: 'block',
          width: '100%',
        }}
      >
        {
          (includeHorseTaskTypeTagId || includeBarnTaskTypeTagId) && (
            <Button
              size="small"
              sx={{ width: '100%' }}
              onClick={() => { navigate('/settings/tasks') }}
              onMouseDown={(event) => event.preventDefault()}
            >
              Manage Tasks
            </Button>
          )
        }
        {includeEventTaskTypeTagId && (
          <Button
            size="small"
            sx={{ width: '100%' }}
            onClick={() => { navigate('/settings/calendar') }}
            onMouseDown={(event) => event.preventDefault()}
          >
            Manage Calendars
          </Button>
        )}
      </div>
    </Paper>
  );
};

const TaskDefinitionAutocomplete = ({
  label,
  onSelect,
  value,
  validation,
  exludeTagIds,
  includeHorseTaskTypeTagId,
  includeBarnTaskTypeTagId,
  includeEventTaskTypeTagId,
  eventTemplates,
  limitTags,
  size,
  disabled,
  multiple,
  icon,
  placeholder,
  helperText,
  ...rest
}) => {
  // React state
  const [search, setSearch] = React.useState('');

  React.useEffect(() => {
    setSearch('')
  }, [value])

  // Redux state
  const allEventTemplates = useSelector((state) => state.eventTemplate.eventTemplates);
  const selectedBarn = useSelector((state) => state.barn.selectedBarn);
  const permissions = useSelector((state) => state.user.permissions);

  const sourceEventTemplates = Array.isArray(eventTemplates) && eventTemplates.length > 0
    ? eventTemplates
    : allEventTemplates;
  let filteredEventTemplates = sourceEventTemplates || [];
  if (!includeHorseTaskTypeTagId) {
    filteredEventTemplates = filteredEventTemplates
      .filter((et) => et.tags.map((t) => t.id).indexOf(selectedBarn.barnMetadata.horseTaskTypeTagId) < 0);
  }
  if (!includeBarnTaskTypeTagId) {
    filteredEventTemplates = filteredEventTemplates
      .filter((et) => et.tags.map((t) => t.id).indexOf(selectedBarn.barnMetadata.barnTaskTypeTagId) < 0);
  }
  if (!includeEventTaskTypeTagId) {
    filteredEventTemplates = filteredEventTemplates
      .filter((et) => et.tags.map((t) => t.id).indexOf(selectedBarn.barnMetadata.eventTaskTypeTagId) < 0);
  }

  const adornmentIcon = icon && (includeEventTaskTypeTagId && !includeBarnTaskTypeTagId && !includeHorseTaskTypeTagId)
    ? <Today color={secondary.main} />
    : <Task sx={{ color: (value && value.eventDataDefaultValues && value.eventDataDefaultValues.calendarDisplayColor) || secondary.main }} />

  const normalizedValue = React.useMemo(
    () => {
      if (multiple) {
        if (Array.isArray(value)) return value;
        if (value) return [value];
        return [];
      }
      return value || null;
    },
    [value, multiple],
  );
  const selectedValues = multiple
    ? normalizedValue
    : [];

  return (
    <Autocomplete
      id="event-template-select-autocomplete"
      multiple={multiple}
      onChange={onSelect}
      disableCloseOnSelect={multiple}
      size={size}
      onBlur={() => setSearch('')}
      disableClearable={multiple}
      filterOptions={(x) => x}
      clearOnBlur={!multiple}
      sx={{
        '& .MuiAutocomplete-inputRoot': {
          alignItems: multiple
            ? 'flex-start'
            : 'center',
          flexWrap: multiple
            ? 'wrap'
            : 'nowrap',
          paddingTop: multiple && selectedValues.length
            ? 1
            : undefined,
          gap: multiple
            ? 0.5
            : 0,
        },
        '& .MuiInputBase-root': multiple && { height: '100% !important' },
        '& .MuiChip-root': {
          height: size === 'small'
            ? 24
            : 32,
        },
      }}
      {...rest}
      limitTags={limitTags}
      options={filteredEventTemplates.filter((et) => et.name.toLowerCase().indexOf(search.toLowerCase()) >= 0
        && exludeTagIds.indexOf(et.id) < 0).sort((a, b) => -(b.name).localeCompare(a.name))}
      value={normalizedValue}
      disabled={disabled}
      isOptionEqualToValue={(option, valueCompare) => option.id === valueCompare.id}
      getOptionLabel={(option) => option.name}
      renderOption={(props, option) => (
        <MenuItem
          {...props}
          sx={{
            width: '100%',
            // borderLeft: `6px solid ${(option.eventDataDefaultValues && option.eventDataDefaultValues.calendarDisplayColor) || '#A9A9A9'}`,
            // borderBottom: `1px solid ${'var(--blueGrey-unknown)'}`,
          }}
          style={{
            '--blueGrey-unknown': blueGrey[500],
            // eslint-disable-next-line react/prop-types
            ...props.style,
          }}
        >
          <Box sx={{ mr: 1 }}>
            {
              option.tags.find((t) => t.id === selectedBarn.barnMetadata.eventTaskTypeTagId)
                ? (
                  <Today sx={{ color: (option.eventDataDefaultValues && option.eventDataDefaultValues.calendarDisplayColor) || '#A9A9A9' }} />
                )
                : (
                  <Task sx={{ color: (option.eventDataDefaultValues && option.eventDataDefaultValues.calendarDisplayColor) || '#A9A9A9' }} />
                )
            }
          </Box>
          <ListItemText
            component="div"
            primary={option.name}
          // secondaryTypographyProps={{ component: 'div' }}
          // secondary={<TagList tags={option.tags} />}
          />
        </MenuItem>
      )}
      renderTags={(v, getTagProps) => v.map((option, index) => (
        <Chip
          key={`et-chip-autocomplete-${option.id}-${label}`}
          {...getTagProps({ index })}
          size={size}
          label={option.name}
        />
      ))}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          fullWidth
          onChange={(e) => { setSearch(e.target.value); }}
          placeholder={placeholder || 'Search...'}
          error={!!validation}
          helperText={(validation && validation) || helperText}
          InputProps={{
            ...(params.InputProps || {}),
            startAdornment: params.InputProps.startAdornment || (icon && (
              <InputAdornment position="start">
                {adornmentIcon}
              </InputAdornment>
            )),
          }}
        />
      )}
      slots={{
        paper: ((permissions.owner || permissions.admin))
          ? CustomPaper
          : undefined,
      }}
      slotProps={{
        paper: {
          includeBarnTaskTypeTagId,
          includeHorseTaskTypeTagId,
          includeEventTaskTypeTagId,
        },
      }}
    />
  );
};

TaskDefinitionAutocomplete.propTypes = {
  label: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  value: PropTypes.any,
  validation: PropTypes.string,
  eventTemplates: PropTypes.array,
  exludeTagIds: PropTypes.array,
  includeHorseTaskTypeTagId: PropTypes.bool,
  includeBarnTaskTypeTagId: PropTypes.bool,
  includeEventTaskTypeTagId: PropTypes.bool,
  size: PropTypes.string,
  limitTags: PropTypes.number,
  disabled: PropTypes.bool,
  multiple: PropTypes.bool,
  icon: PropTypes.bool,
  placeholder: PropTypes.string,
  helperText: PropTypes.string,
};

TaskDefinitionAutocomplete.defaultProps = {
  label: 'Task',
  value: null,
  validation: null,
  eventTemplates: [],
  exludeTagIds: [],
  includeHorseTaskTypeTagId: true,
  includeBarnTaskTypeTagId: true,
  includeEventTaskTypeTagId: false,
  size: 'default',
  disabled: false,
  limitTags: 5,
  multiple: false,
  icon: false,
  placeholder: null,
  helperText: null,
};

export default TaskDefinitionAutocomplete;
